/**
 * api/own-photo.ts — HTTP contract for the own-photo signed-upload flow.
 *
 * Covers:
 *   POST /v1/own-photo/upload-url  → CreateOwnPhotoUploadUrlRequest / Response
 *   POST /v1/own-photo/read-url    → CreateOwnPhotoReadUrlRequest / Response
 *
 * The client never writes to Storage directly with an anon key; this endpoint
 * is the only public gate. It validates the declared mime/size, mints
 * `own-photo/{uuid}.{ext}` in the PRIVATE `wandero-private` bucket, and returns
 * a one-shot signed-upload token
 * (`storage.from('wandero-private').createSignedUploadUrl(path)`,
 * service-role). The client then uploads directly to Storage with
 * `supabase.storage.from('wandero-private').uploadToSignedUrl(path, token,
 * file)`.
 *
 * Because the bucket is private (customer photos are PII — sec-review
 * FINDING #1), it has NO public read URL. Every client-facing view (cart
 * thumbnail across reload) resolves a short-lived signed-read URL via
 * `POST /v1/own-photo/read-url` (service-role `createSignedUrl`, ~300s TTL).
 *
 * Source: 2026-07-18-custom-photo-order-page spec
 * §"Storage / upload surface (решение)" + Risk R1 / R2, T11/T12.
 */

import { z } from 'zod';

/**
 * MIME allowlist for own-photo uploads (spec Risk R1 mitigation) — tighter
 * than the `wandero` bucket's raw `allowed_mime_types` (which additionally
 * permits avif; AVIF upload support is explicitly out of scope for this
 * flow). Enforced here (api validates before minting the signed-upload URL)
 * AND independently, as a backstop, by Supabase Storage's bucket config.
 */
export const OWN_PHOTO_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/tiff',
] as const;
export const OwnPhotoMimeType = z.enum(OWN_PHOTO_ALLOWED_MIME_TYPES);
export type OwnPhotoMimeType = z.infer<typeof OwnPhotoMimeType>;

/**
 * Storefront upload cap — spec §Open Questions Q2 recommendation (≤25 MB),
 * tighter than the bucket's raw 100 MB `file_size_limit`. Single source of
 * truth: both the api route's request validation and the storage-sign call
 * (T3) read this constant — never hard-code the number elsewhere.
 */
export const OWN_PHOTO_MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

// ── POST /v1/own-photo/upload-url ───────────────────────────────────────────

export const CreateOwnPhotoUploadUrlRequestSchema = z.object({
  /** Original filename as picked by the shopper — display/extension-derivation only, never trusted verbatim for the storage path. */
  filename: z.string().min(1).max(260),
  content_type: OwnPhotoMimeType,
  /** Client-declared size — advisory pre-flight rejection; Storage's own `file_size_limit` is the hard backstop that can't be spoofed. */
  size_bytes: z.number().int().positive().max(OWN_PHOTO_MAX_UPLOAD_BYTES),
});
export type CreateOwnPhotoUploadUrlRequest = z.infer<typeof CreateOwnPhotoUploadUrlRequestSchema>;

export const CreateOwnPhotoUploadUrlResponseSchema = z.object({
  /** `own-photo/{uuid}.{ext}` — the freshly minted storage path. This is the
   * same string that ends up in `CartItem.original.path` /
   * `OrderItemSnapshot.original.path` (see `schemas/own-photo.ts`,
   * `schemas/cart.ts`, `schemas/orders.ts`). */
  path: z.string().min(1),
  /** One-shot signed-upload token, passed to
   * `supabase.storage.from('wandero-private').uploadToSignedUrl(path, token,
   * file)`. */
  token: z.string().min(1),
});
export type CreateOwnPhotoUploadUrlResponse = z.infer<typeof CreateOwnPhotoUploadUrlResponseSchema>;

// ── POST /v1/own-photo/read-url ─────────────────────────────────────────────

/**
 * An own-photo storage path is always `own-photo/{uuid}.{ext}` — a single
 * `own-photo/` prefix segment then a flat filename, no nested slashes and no
 * `..`. Constraining the read-url endpoint to exactly this shape keeps it from
 * becoming an open signing oracle for arbitrary objects in the private bucket:
 * only own-photo originals can ever be signed, never (say) another tenant's
 * private path. Mock refs (`own-photo/mock-…`) also match.
 */
export const OWN_PHOTO_PATH_PATTERN = /^own-photo\/[A-Za-z0-9._-]+$/;

/** Default signed-read TTL — short-lived by design (PII). Single source of truth. */
export const OWN_PHOTO_READ_URL_TTL_SECONDS = 300;

export const CreateOwnPhotoReadUrlRequestSchema = z.object({
  /** The stored own-photo object path (`own-photo/{uuid}.{ext}`) — exactly the
   * `CartItem.original.path` / `OrderItemSnapshot.original.path` value. */
  path: z.string().min(1).max(300).regex(OWN_PHOTO_PATH_PATTERN),
});
export type CreateOwnPhotoReadUrlRequest = z.infer<typeof CreateOwnPhotoReadUrlRequestSchema>;

export const CreateOwnPhotoReadUrlResponseSchema = z.object({
  /** Short-lived signed-read URL for the own-photo original. Same-origin
   * RELATIVE path (`/storage/v1/object/sign/wandero-private/…?token=…`) — the
   * browser fetches it against the storefront origin (traefik proxies
   * `/storage/*` to Supabase Storage), matching the project's storage-URL
   * convention. Expires after `expires_in` seconds. */
  url: z.string().min(1),
  /** TTL in seconds the returned `url` stays valid. */
  expires_in: z.number().int().positive(),
});
export type CreateOwnPhotoReadUrlResponse = z.infer<typeof CreateOwnPhotoReadUrlResponseSchema>;
